import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { Tables } from '@/integrations/supabase/types';

// Types for our download flow state
export interface DownloadSession {
  sequenceId: string;
  sequenceTitle: string;
  sequenceArtist: string;
  isOwner: boolean;
  isFree: boolean;
  price?: number;
  purchaseId?: string;
  userId?: string;
  isCharity?: boolean;
  charityName?: string;
  createdAt: Date;
  expiresAt: Date;
}

export interface ModalState {
  isAuthModalVisible: boolean;
  isDonationModalVisible: boolean;
  isAddedToDisplayModalVisible: boolean;
  isConfirmationPageVisible: boolean;
}

export interface DownloadProgress {
  isGeneratingUrl: boolean;
  isDownloading: boolean;
  progress: number;
  error: string | null;
  downloadUrl: string | null;
  token: string | null;
}

export interface UserPreferences {
  suppressAddedToDisplayModal: boolean;
  defaultCharityId: string | null;
  defaultCharity?: Tables<'charities'> | null;
}

// Main store interface
interface DownloadFlowStore {
  // State
  currentSession: DownloadSession | null;
  modalState: ModalState;
  downloadProgress: DownloadProgress;
  userPreferences: UserPreferences;
  isInitialized: boolean;

  // Session management
  startDownloadSession: (params: {
    sequenceId: string;
    sequenceTitle: string;
    sequenceArtist: string;
    isOwner: boolean;
    isFree: boolean;
    price?: number;
    purchaseId?: string;
    userId?: string;
    isCharity?: boolean;
    charityName?: string;
  }) => void;
  
  endDownloadSession: () => void;
  
  // Modal state management
  showAuthModal: () => void;
  hideAuthModal: () => void;
  showDonationModal: () => void;
  hideDonationModal: () => void;
  showAddedToDisplayModal: () => void;
  hideAddedToDisplayModal: () => void;
  showConfirmationPage: () => void;
  hideConfirmationPage: () => void;
  hideAllModals: () => void;

  // Download progress management
  setGeneratingUrl: (isGenerating: boolean) => void;
  setDownloading: (isDownloading: boolean, progress?: number) => void;
  setDownloadProgress: (progress: number) => void;
  setDownloadUrl: (url: string, token: string) => void;
  setDownloadError: (error: string | null) => void;
  resetDownloadProgress: () => void;

  // User preferences management
  setUserPreferences: (preferences: Partial<UserPreferences>) => void;
  setSuppressAddedToDisplayModal: (suppress: boolean) => void;
  setDefaultCharity: (charity: Tables<'charities'> | null) => void;

  // Flow control helpers
  shouldShowDonationModal: () => boolean;
  shouldShowAddedToDisplayModal: () => boolean;
  getNextModalInFlow: () => 'donation' | 'addedToDisplay' | 'confirmation' | null;
  
  // Utility methods
  isSessionExpired: () => boolean;
  getTimeUntilExpiration: () => number;
  markAddedToDisplayModalShown: () => void;
  reset: () => void;
}

// Default states
const defaultModalState: ModalState = {
  isAuthModalVisible: false,
  isDonationModalVisible: false,
  isAddedToDisplayModalVisible: false,
  isConfirmationPageVisible: false,
};

const defaultDownloadProgress: DownloadProgress = {
  isGeneratingUrl: false,
  isDownloading: false,
  progress: 0,
  error: null,
  downloadUrl: null,
  token: null,
};

const defaultUserPreferences: UserPreferences = {
  suppressAddedToDisplayModal: false,
  defaultCharityId: null,
  defaultCharity: null,
};

// Create the download flow store with persistence
export const useDownloadFlowStore = create<DownloadFlowStore>()(
  persist(
    (set, get) => ({
      // Initial state
      currentSession: null,
      modalState: defaultModalState,
      downloadProgress: defaultDownloadProgress,
      userPreferences: defaultUserPreferences,
      isInitialized: false,

      // Session management
      startDownloadSession: (params) => {
        const now = new Date();
        const expiresAt = new Date(now.getTime() + 30 * 60 * 1000); // 30 minutes

        set({
          currentSession: {
            ...params,
            createdAt: now,
            expiresAt,
          },
          modalState: defaultModalState,
          downloadProgress: defaultDownloadProgress,
          isInitialized: true,
        });
      },

      endDownloadSession: () => {
        set({
          currentSession: null,
          modalState: defaultModalState,
          downloadProgress: defaultDownloadProgress,
        });
      },

      // Modal state management
      showAuthModal: () =>
        set((state) => ({
          modalState: { ...state.modalState, isAuthModalVisible: true },
        })),

      hideAuthModal: () =>
        set((state) => ({
          modalState: { ...state.modalState, isAuthModalVisible: false },
        })),

      showDonationModal: () =>
        set((state) => ({
          modalState: { ...state.modalState, isDonationModalVisible: true },
        })),

      hideDonationModal: () =>
        set((state) => ({
          modalState: { ...state.modalState, isDonationModalVisible: false },
        })),

      showAddedToDisplayModal: () =>
        set((state) => ({
          modalState: { ...state.modalState, isAddedToDisplayModalVisible: true },
        })),

      hideAddedToDisplayModal: () =>
        set((state) => ({
          modalState: { ...state.modalState, isAddedToDisplayModalVisible: false },
        })),

      showConfirmationPage: () =>
        set((state) => ({
          modalState: { ...state.modalState, isConfirmationPageVisible: true },
        })),

      hideConfirmationPage: () =>
        set((state) => ({
          modalState: { ...state.modalState, isConfirmationPageVisible: false },
        })),

      hideAllModals: () =>
        set({ modalState: defaultModalState }),

      // Download progress management
      setGeneratingUrl: (isGenerating) =>
        set((state) => ({
          downloadProgress: { ...state.downloadProgress, isGeneratingUrl: isGenerating },
        })),

      setDownloading: (isDownloading, progress = 0) =>
        set((state) => ({
          downloadProgress: {
            ...state.downloadProgress,
            isDownloading,
            progress: isDownloading ? progress : 0,
          },
        })),

      setDownloadProgress: (progress) =>
        set((state) => ({
          downloadProgress: { ...state.downloadProgress, progress },
        })),

      setDownloadUrl: (url, token) =>
        set((state) => ({
          downloadProgress: {
            ...state.downloadProgress,
            downloadUrl: url,
            token,
            error: null,
          },
        })),

      setDownloadError: (error) =>
        set((state) => ({
          downloadProgress: {
            ...state.downloadProgress,
            error,
            isGeneratingUrl: false,
            isDownloading: false,
          },
        })),

      resetDownloadProgress: () =>
        set({ downloadProgress: defaultDownloadProgress }),

      // User preferences management
      setUserPreferences: (preferences) =>
        set((state) => ({
          userPreferences: { ...state.userPreferences, ...preferences },
        })),

      setSuppressAddedToDisplayModal: (suppress) =>
        set((state) => ({
          userPreferences: { ...state.userPreferences, suppressAddedToDisplayModal: suppress },
        })),

      setDefaultCharity: (charity) =>
        set((state) => ({
          userPreferences: {
            ...state.userPreferences,
            defaultCharity: charity,
            defaultCharityId: charity?.id || null,
          },
        })),

      // Flow control helpers
      shouldShowDonationModal: () => {
        const state = get();
        return (
          state.currentSession?.isCharity === true &&
          state.currentSession?.charityName &&
          !state.modalState.isDonationModalVisible
        );
      },

      shouldShowAddedToDisplayModal: () => {
        const state = get();
        return (
          !state.userPreferences.suppressAddedToDisplayModal &&
          !state.modalState.isAddedToDisplayModalVisible
        );
      },

      getNextModalInFlow: () => {
        const state = get();
        
        if (state.shouldShowDonationModal()) {
          return 'donation';
        }
        
        if (state.shouldShowAddedToDisplayModal()) {
          return 'addedToDisplay';
        }
        
        return 'confirmation';
      },

      // Utility methods
      isSessionExpired: () => {
        const state = get();
        if (!state.currentSession) return true;
        return new Date() > state.currentSession.expiresAt;
      },

      getTimeUntilExpiration: () => {
        const state = get();
        if (!state.currentSession) return 0;
        return Math.max(0, state.currentSession.expiresAt.getTime() - Date.now());
      },

      markAddedToDisplayModalShown: () => {
        // This method is called when the AddedToDisplayModal is shown/dismissed
        // Can be used for analytics, flow tracking, or state management
        console.log('Added to Display modal was shown/dismissed');
      },

      reset: () =>
        set({
          currentSession: null,
          modalState: defaultModalState,
          downloadProgress: defaultDownloadProgress,
          userPreferences: defaultUserPreferences,
          isInitialized: false,
        }),
    }),
    {
      name: 'download-flow-storage',
      // Only persist certain parts of the state
      partialize: (state) => ({
        userPreferences: state.userPreferences,
        currentSession: state.currentSession,
        isInitialized: state.isInitialized,
      }),
      // Custom deserialization to handle Date objects
      onRehydrateStorage: () => (state) => {
        if (state?.currentSession) {
          // Convert date strings back to Date objects
          state.currentSession.createdAt = new Date(state.currentSession.createdAt);
          state.currentSession.expiresAt = new Date(state.currentSession.expiresAt);
          
          // Check if session is expired on rehydration
          if (new Date() > state.currentSession.expiresAt) {
            state.currentSession = null;
            state.modalState = defaultModalState;
            state.downloadProgress = defaultDownloadProgress;
          }
        }
      },
    }
  )
); 